import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaSyncAlt, FaChevronDown, FaChevronUp, FaTimes } from "react-icons/fa";
import style from "./ProcessingQueue.module.css";
import {
  getUploadedExcelsList,
  cancelBulkUpload,
  getGroupedFailures,
  retryFailuresByCause,
} from "../../../api/bulk-upload-api";
import { formatBytes } from "../../../utils/helperFunctions/formatBytes";
import { formatDate } from "../../../utils/dateFormatter";
import { showToast } from "../../../utils/Toastify/Toastify";

const POLL_INTERVAL_MS = 5000;
const QUEUE_LIST_SIZE = 50;

const STATUS_CARDS = [
  { key: "queued", label: "Queued", sub: "waiting for a slot" },
  { key: "processing", label: "Processing", sub: "running now" },
  { key: "completed", label: "Completed", sub: "recent uploads" },
  { key: "failed", label: "Failed", sub: "needs attention" },
];

// estimated_completion_at is a timestamp from the API, not something derived
// here on every render, so reading the clock once per call is fine.
const getTimeLeft = (estimatedCompletionAt) => {
  if (!estimatedCompletionAt) return null;
  const diffMs = new Date(estimatedCompletionAt).getTime() - Date.now();
  if (!Number.isFinite(diffMs) || diffMs <= 0) return null;
  const totalSeconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s left` : `${seconds}s left`;
};

// Short, real tag for the thumb box — no fake video resolution (we don't have
// that), just the upload_type we already store.
const UPLOAD_TYPE_LABEL = {
  zip: "ZIP",
  csv: "CSV",
  series_excel: "CSV",
};

const FileTypeBadge = ({ uploadType }) => (
  <span>{UPLOAD_TYPE_LABEL[uploadType] || "FILE"}</span>
);

const ProcessingQueue = () => {
  const [excelList, setExcelList] = useState([]);
  const [summary, setSummary] = useState({});
  const [failureGroups, setFailureGroups] = useState([]);
  const [totalFailedFiles, setTotalFailedFiles] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [inFlightFilter, setInFlightFilter] = useState("all");
  const [expandedCause, setExpandedCause] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [retryingCause, setRetryingCause] = useState(null);
  // Ids of uploads retried this session, most-recent-first. fetchQueue()
  // replaces excelList wholesale in the backend's created_at order on every
  // poll, which would otherwise snap a retried upload back to its old
  // position the moment it flips from processing to completed — this keeps
  // it pinned to the top through that whole transition.
  const [recentlyRetriedIds, setRecentlyRetriedIds] = useState([]);

  const pollingRef = useRef(null);

  const fetchQueue = useCallback(async ({ silent } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const [listResponse, groupsResponse] = await Promise.all([
        getUploadedExcelsList(QUEUE_LIST_SIZE, 1),
        getGroupedFailures(),
      ]);
      const data = listResponse?.data?.data || [];
      setExcelList(data);
      setSummary(listResponse?.data?.summary || {});
      setFailureGroups(groupsResponse?.data?.data || []);
      setTotalFailedFiles(groupsResponse?.data?.total_failed || 0);
      setLastUpdated(new Date());

      const shouldContinue = data.some((item) => item.should_continue_polling);
      if (shouldContinue) {
        if (!pollingRef.current) {
          pollingRef.current = setInterval(
            () => fetchQueue({ silent: true }),
            POLL_INTERVAL_MS
          );
        }
      } else if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    } catch (error) {
      console.error("Error fetching processing queue:", error);
    } finally {
      if (!silent) setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchQueue();
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [fetchQueue]);

  const statusOf = (item) => item?.status?.toLowerCase();

  // Pulls anything in recentlyRetriedIds to the front (in retry order),
  // leaving everyone else in the backend's created_at order.
  const withRetriedFirst = (items) => {
    if (!recentlyRetriedIds.length) return items;
    const byId = new Map(items.map((item) => [item.id, item]));
    const retried = recentlyRetriedIds
      .map((id) => byId.get(id))
      .filter(Boolean);
    const retriedIdSet = new Set(retried.map((item) => item.id));
    const rest = items.filter((item) => !retriedIdSet.has(item.id));
    return [...retried, ...rest];
  };

  const inFlight = excelList.filter((item) =>
    ["queued", "processing"].includes(statusOf(item))
  );
  const failedBatches = excelList.filter((item) => statusOf(item) === "failed");
  const completedInList = excelList.filter((item) => statusOf(item) === "completed");

  // "All" also includes completed uploads (most-recent-first, since excelList
  // is already ordered that way) so finished files don't just vanish from the
  // panel — "Running" stays scoped to what's actually in progress right now.
  const inFlightAndCompleted = withRetriedFirst(
    excelList.filter((item) =>
      ["queued", "processing", "completed"].includes(statusOf(item))
    )
  );
  const visibleInFlight =
    inFlightFilter === "running"
      ? withRetriedFirst(
          inFlight.filter((item) => statusOf(item) === "processing")
        )
      : inFlightAndCompleted;

  // The backend's summary only reports a merged "processing_uploads" bucket
  // (queued + processing combined, per BulkUploadController.getUploadSummary)
  // with no separate queued count — so the queued/processing split has to
  // come from this fetched page, not the global summary, or the two cards
  // would double-count against each other.
  const counts = {
    queued: inFlight.filter((item) => statusOf(item) === "queued").length,
    processing: inFlight.filter((item) => statusOf(item) === "processing")
      .length,
    completed: summary?.completed_uploads ?? completedInList.length,
    failed: summary?.failed_uploads ?? failedBatches.length,
  };
  const totalCount =
    counts.queued + counts.processing + counts.completed + counts.failed;

  const handleCancel = async (id) => {
    setCancellingId(id);
    try {
      const response = await cancelBulkUpload(id);
      showToast(response?.data?.message || "Upload cancelled", "success");
      await fetchQueue({ silent: true });
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to cancel upload",
        "error"
      );
    } finally {
      setCancellingId(null);
    }
  };

  const handleRetryCause = async (cause) => {
    setRetryingCause(cause);

    // Small imports finish in 2-4s — faster than our 5s poll — so waiting for
    // the next fetch to show "processing" often skips straight to the result.
    // We already know which uploads this retry targets (each failure group
    // carries bulk_upload_id on its tasks), so flip them locally right away;
    // the next fetchQueue() below corrects it to whatever actually happened.
    const affectedIds = new Set(
      (cause === "all"
        ? failureGroups
        : failureGroups.filter((group) => group.cause === cause)
      ).flatMap((group) => group.tasks.map((task) => task.bulk_upload_id))
    );
    if (affectedIds.size) {
      setExcelList((prev) =>
        prev.map((item) =>
          affectedIds.has(item.id)
            ? { ...item, status: "processing", progress_percentage: "0.00" }
            : item
        )
      );
      // Pin these to the top (via withRetriedFirst) through the whole
      // processing → completed transition, not just this optimistic flip.
      setRecentlyRetriedIds((prev) => [
        ...affectedIds,
        ...prev.filter((id) => !affectedIds.has(id)),
      ]);
    }

    try {
      const response = await retryFailuresByCause(cause);
      showToast(response?.data?.message || "Retry started", "success");
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to retry",
        "error"
      );
    } finally {
      await fetchQueue({ silent: true });
      setRetryingCause(null);
    }
  };

  return (
    <div className={style.container}>
      <div className={style.header}>
        <div>
          <h2 className={style.title}>Processing Queue</h2>
          <p className={style.description}>
            Monitor and manage batch upload jobs
          </p>
        </div>
        <div className={style.headerMeta}>
          {lastUpdated && (
            <span className={style.lastUpdated}>
              Updated {formatDate(lastUpdated.toISOString(), "time")}
            </span>
          )}
          <button
            type="button"
            className={style.refreshBtn}
            onClick={() => fetchQueue()}
            title="Refresh"
          >
            <FaSyncAlt className={refreshing ? style.refreshSpin : ""} />
          </button>
        </div>
      </div>

      <div className={style.statusCards}>
        {STATUS_CARDS.map(({ key, label, sub }) => {
          const value = counts[key] || 0;
          const share = totalCount > 0 ? (value / totalCount) * 100 : 0;
          return (
            <div key={key} className={style.statusCard}>
              <div className={style.statusCardTop}>
                <span className={style.statusLabel}>{label}</span>
                <span className={`${style.statusDot} ${style[key]}`} />
              </div>
              <div className={style.statusCardMain}>
                <span className={`${style.statusValue} ${style[key]}`}>
                  {value}
                </span>
                <span className={style.statusSub}>{sub}</span>
              </div>
              <div className={style.statusBarTrack}>
                <div
                  className={`${style.statusBarFill} ${style[key]}`}
                  style={{ width: `${share}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className={style.panels}>
        <div className={style.panel}>
          <div className={style.panelHeader}>
            <h3 className={style.panelTitle}>In flight</h3>
            <span className={style.panelCount}>{visibleInFlight.length}</span>
            <div className={style.filterTabs}>
              <button
                type="button"
                className={`${style.filterTab} ${
                  inFlightFilter === "all" ? style.filterTabActive : ""
                }`}
                onClick={() => setInFlightFilter("all")}
              >
                All
              </button>
              <button
                type="button"
                className={`${style.filterTab} ${
                  inFlightFilter === "running" ? style.filterTabActive : ""
                }`}
                onClick={() => setInFlightFilter("running")}
              >
                Running
              </button>
            </div>
          </div>
          {visibleInFlight.length === 0 ? (
            <p className={style.emptyState}>
              {inFlight.length === 0
                ? "No files currently processing."
                : "Nothing running right now."}
            </p>
          ) : (
            <div className={style.list}>
              {visibleInFlight.map((item) => {
                const st = statusOf(item);
                // progress_percentage comes back as a string (e.g. "42.00")
                // on the list endpoint — round it for a clean display.
                const percent = Math.round(Number(item.progress_percentage)) || 0;
                const isCompleted = st === "completed";
                const timeLeft = isCompleted
                  ? null
                  : getTimeLeft(item.estimated_completion_at);
                const isCancelling = cancellingId === item.id;
                return (
                  <div key={item.id} className={style.jobCard}>
                    <div className={style.jobTop}>
                      <div className={style.jobThumb}>
                        <FileTypeBadge uploadType={item.upload_type} />
                      </div>
                      <div className={style.jobInfo}>
                        <div
                          className={style.jobName}
                          title={item.original_filename}
                        >
                          {item.original_filename}
                        </div>
                        <div className={style.jobMeta}>
                          {formatBytes(item.file_size_bytes)}
                          {" · "}
                          <span className={`${style.statusBadge} ${style[st]}`}>
                            {item.status}
                          </span>
                        </div>
                      </div>
                      <div className={style.jobRight}>
                        <div className={`${style.jobPercent} ${style[st]}`}>
                          {percent}%
                        </div>
                        {timeLeft && (
                          <div className={style.jobTimeLeft}>{timeLeft}</div>
                        )}
                        {isCompleted && item.completed_at && (
                          <div className={style.jobTimeLeft}>
                            Done {formatDate(item.completed_at, "time")}
                          </div>
                        )}
                      </div>
                      {!isCompleted && (
                        <button
                          type="button"
                          className={style.cancelBtn}
                          title="Cancel upload"
                          onClick={() => handleCancel(item.id)}
                          disabled={isCancelling}
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                    <div className={style.progressTrack}>
                      <div
                        className={`${style.progressFill} ${style[st]}`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={style.panel}>
          <div className={style.panelHeader}>
            <h3 className={style.panelTitle}>Needs attention</h3>
            <span
              className={`${style.panelCount} ${
                totalFailedFiles ? style.panelCountWarn : ""
              }`}
            >
              {totalFailedFiles}
            </span>
            <button
              type="button"
              className={style.retryAllBtn}
              onClick={() => handleRetryCause("all")}
              disabled={!failureGroups.length || retryingCause === "all"}
            >
              {retryingCause === "all" ? "Retrying…" : "Retry all"}
            </button>
          </div>
          <p className={style.panelSubtitle}>
            Grouped by cause so one fix clears many files.
          </p>
          {failureGroups.length === 0 ? (
            <p className={style.emptyState}>No failed uploads.</p>
          ) : (
            <div className={style.list}>
              {failureGroups.map((group) => {
                const isExpanded = expandedCause === group.cause;
                const isRetrying = retryingCause === group.cause;
                return (
                  <div key={group.cause} className={style.failCard}>
                    <div className={style.failTop}>
                      <div>
                        <div className={style.failName}>{group.label}</div>
                        {group.hint && (
                          <div className={style.failMeta}>{group.hint}</div>
                        )}
                      </div>
                      <span className={style.failCountBadge}>
                        {group.count} file{group.count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className={style.failActions}>
                      <button
                        type="button"
                        className={style.retryBtn}
                        onClick={() => handleRetryCause(group.cause)}
                        disabled={isRetrying || !group.retryable_count}
                      >
                        {isRetrying ? "Retrying…" : "Retry"}
                      </button>
                      <button
                        type="button"
                        className={style.viewFilesBtn}
                        onClick={() =>
                          setExpandedCause(isExpanded ? null : group.cause)
                        }
                      >
                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        View files
                      </button>
                    </div>
                    {isExpanded && (
                      <div className={style.failRows}>
                        {group.tasks.map((task) => (
                          <div key={task.id} className={style.failRow}>
                            <span className={style.failRowTitle}>
                              {task.filename || `Row ${task.row_number}`}
                              {task.title ? ` — ${task.title}` : ""}
                            </span>
                            {task.filename && (
                              <span className={style.failRowMeta}>
                                Row {task.row_number}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcessingQueue;
